import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import { appAlert, appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

const STATUS_BADGES = {
    pending: { class: 'bg-warning text-dark', icon: 'fa-clock', labelId: 'dyrejournal.idexx.statusPending', labelFallback: 'Pending' },
    submitted: { class: 'bg-info', icon: 'fa-paper-plane', labelId: 'dyrejournal.idexx.statusSubmitted', labelFallback: 'Submitted' },
    completed: { class: 'bg-success', icon: 'fa-check-circle', labelId: 'dyrejournal.idexx.statusCompleted', labelFallback: 'Completed' },
    cancelled: { class: 'bg-secondary', icon: 'fa-times-circle', labelId: 'dyrejournal.idexx.statusCancelled', labelFallback: 'Cancelled' },
    error: { class: 'bg-danger', icon: 'fa-triangle-exclamation', labelId: 'dyrejournal.idexx.statusError', labelFallback: 'Error' },
};

@withText(props => ({
    cancelOrderTitle: <Text id='dyrejournal.idexx.cancelOrder'>Cancel order</Text>,
    cancelConfirm: <Text id='dyrejournal.idexx.cancelConfirm'>Cancel this IDEXX order?</Text>,
    cancelFailed: <Text id='dyrejournal.idexx.cancelFailed'>Failed to cancel order</Text>,
}))
@observer
class IdexxOrderStatus extends Component {
    constructor(props) {
        super(props);
        this.state = {
            orders: [],
            loading: false,
            expandedId: null,
        };
    }

    componentDidMount() {
        this.loadOrders();
        // Refresh on focus / visibility return so coming back from the
        // VetConnect Plus tab updates badges immediately. No background
        // polling — vets can use the manual refresh button if needed, and
        // the focus listener already covers the common "I just clicked
        // Order in VC+ and tabbed back" path.
        if (typeof window !== 'undefined') {
            this._onFocus = () => {
                if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
                    this.loadOrders({ silent: true });
                }
            };
            window.addEventListener('focus', this._onFocus);
            if (typeof document !== 'undefined') {
                document.addEventListener('visibilitychange', this._onFocus);
            }
        }
    }

    componentWillUnmount() {
        if (typeof window !== 'undefined' && this._onFocus) {
            window.removeEventListener('focus', this._onFocus);
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', this._onFocus);
            }
        }
    }

    loadOrders = async (opts = {}) => {
        const { journal } = this.props;
        // Combine ids from both the journal prop (initial load) AND any
        // orders we've already pulled into state.orders. The latter is how
        // we keep just-created orders fresh on subsequent refreshes —
        // handleOrderCreated puts the new order into state.orders, but the
        // parent's journal prop stays stale until the drawer is closed and
        // reopened, so we can't rely on journal.idexxOrders alone.
        const journalOrderIds = (journal?.idexxOrders || []).map(Number).filter(Boolean);
        const stateOrderIds = (this.state.orders || []).map(o => o && o.id).filter(Boolean);
        const orderIds = Array.from(new Set([...journalOrderIds, ...stateOrderIds]));
        if (orderIds.length === 0) return;

        // `silent: true` skips the spinner so the focus refresh doesn't
        // visually flash the rows the user is reading.
        if (!opts.silent) this.setState({ loading: true });
        const orders = [];

        for (const orderId of orderIds) {
            try {
                const response = await util.fetchApi(`/api/integrations/idexx/order/${orderId}`);
                if (response?.data?.localOrder) {
                    orders.push(response.data.localOrder);
                }
            } catch (e) {
                console.error(`Failed to load IDEXX order ${orderId}:`, e.message);
            }
        }

        this.setState({ orders, loading: false });
    }

    handleCancel = async (e, orderId) => {
        e.stopPropagation();
        const dialogAction = beginDialogAction(this, 'handleCancel');
        if (!dialogAction) return false;
        try {

            const { cancelConfirm, cancelFailed } = this.props;
            // eslint-disable-next-line no-alert
            if (!((await appConfirm(cancelConfirm)) && dialogAction.isCurrent())) return;

            try {
                await util.fetchApi(`/api/integrations/idexx/order/${orderId}`, { method: 'DELETE' });
                this.loadOrders();
            } catch (err) {
                // eslint-disable-next-line no-alert
                (await appAlert(`${cancelFailed}: ${err.message}`));
            }
        } finally {
            dialogAction.finish();
        }
    }

    toggleExpanded = (orderId) => {
        this.setState(prev => ({
            expandedId: prev.expandedId === orderId ? null : orderId,
        }));
    }

    openOrderForm = () => {
        const { appState } = this.props.stores;
        const { journal, animal, visitor, drawerLevel = 1 } = this.props;
        appState.openDrawerRight('idexxOrderForm', {
            journal,
            animal,
            visitor,
            onOrderCreated: this.handleOrderCreated,
        }, drawerLevel + 1);
    }

    handleOrderCreated = (order) => {
        if (!order) return;
        this.setState(prev => ({
            orders: [order, ...prev.orders.filter(o => o.id !== order.id)],
        }));
    }

    renderDetails = (order) => {
        const specimenDate = order.sampleCollectionDate
            ? new Date(order.sampleCollectionDate).toLocaleDateString()
            : (order.idexxRequest?.specimenCollectionDate || '');
        const submittedDate = order.submittedDate ? new Date(order.submittedDate).toLocaleString() : '';
        const completedDate = order.completedDate ? new Date(order.completedDate).toLocaleString() : '';
        const testCodes = (order.tests || []).map(t => t.testCode || t.name || t).filter(Boolean);
        const uiUrl = order.idexxUiURL || order.idexxResponse?.uiURL || '';
        const pdfUrl = order.idexxPdfURL || order.idexxResponse?.pdfURL || '';
        const resultCount = (order.idexxResults || []).length;
        const errObj = order.idexxError && (order.idexxError.message || order.idexxError.code) ? order.idexxError : null;

        return (
            <div class='mt-2 pt-2 border-top'>
                {errObj && (
                    <div class='alert alert-danger py-2 px-3 mb-2' role='alert'>
                        <div class='fw-bold'>
                            <i class='fa-solid fa-triangle-exclamation me-1' />
                            <Text id='dyrejournal.idexx.idexxError'>IDEXX error</Text>
                            {errObj.code ? ` — ${errObj.code}` : ''}
                        </div>
                        {errObj.message && <div class='small mt-1'>{errObj.message}</div>}
                        {errObj.details && errObj.details !== errObj.message && (
                            <pre class='small mt-1 mb-0' style='white-space: pre-wrap; max-height: 160px; overflow-y: auto;'>{errObj.details}</pre>
                        )}
                    </div>
                )}
                {/* Only show the "still a draft" banner while the order is
                    still CREATED locally (status='pending'). IDEXX keeps the
                    `editable` flag true even after SUBMITTED, so we can't
                    rely on it alone — use the normalized local status. */}
                {order.status === 'pending' && order.idexxEditable && (
                    <div class='alert alert-info py-2 px-3 mb-2' role='alert'>
                        <i class='fa-solid fa-circle-info me-1' />
                        <small>
                            <Text id='dyrejournal.idexx.editableNotice'>
                                This order is still a draft in IDEXX (status CREATED). It won't appear in the VetConnect Plus "In process" list until it's submitted — open it in VetConnect Plus to submit or edit.
                            </Text>
                        </small>
                    </div>
                )}
                <div class='row small g-2'>
                    <div class='col-6'>
                        <div class='text-body-secondary'>
                            <Text id='dyrejournal.idexx.modality'>Type</Text>
                        </div>
                        <div>
                            {order.orderType === 'in_house' ? (
                                <Text id='dyrejournal.idexx.inHouse'>In House</Text>
                            ) : (
                                <Text id='dyrejournal.idexx.referenceLab'>Reference Lab</Text>
                            )}
                        </div>
                    </div>
                    {specimenDate && (
                        <div class='col-6'>
                            <div class='text-body-secondary'>
                                <Text id='dyrejournal.idexx.collectionDate'>Collection date</Text>
                            </div>
                            <div>{specimenDate}</div>
                        </div>
                    )}
                    {submittedDate && (
                        <div class='col-6'>
                            <div class='text-body-secondary'>
                                <Text id='dyrejournal.idexx.submittedDate'>Submitted</Text>
                            </div>
                            <div>{submittedDate}</div>
                        </div>
                    )}
                    {completedDate && (
                        <div class='col-6'>
                            <div class='text-body-secondary'>
                                <Text id='dyrejournal.idexx.completedDate'>Completed</Text>
                            </div>
                            <div>{completedDate}</div>
                        </div>
                    )}
                    {order.veterinarianInfo?.name && (
                        <div class='col-6'>
                            <div class='text-body-secondary'>
                                <Text id='dyrejournal.idexx.veterinarian'>Veterinarian</Text>
                            </div>
                            <div>{order.veterinarianInfo.name}</div>
                        </div>
                    )}
                    {order.idexxOrderId && (
                        <div class='col-6'>
                            <div class='text-body-secondary'>
                                <Text id='dyrejournal.idexx.idexxIdLabel'>IDEXX ID</Text>
                            </div>
                            <div>{order.idexxOrderId}</div>
                        </div>
                    )}
                    {order.idexxAccessionId && (
                        <div class='col-6'>
                            <div class='text-body-secondary'>
                                <Text id='dyrejournal.idexx.accessionId'>Accession</Text>
                            </div>
                            <div>{order.idexxAccessionId}</div>
                        </div>
                    )}
                    {testCodes.length > 0 && (
                        <div class='col-12'>
                            <div class='text-body-secondary'>
                                <Text id='dyrejournal.idexx.tests'>Tests</Text>
                            </div>
                            <div>
                                {testCodes.map(code => (
                                    <span key={code} class='badge bg-secondary bg-opacity-25 text-body me-1'>{code}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {order.clinicalNotes && (
                        <div class='col-12'>
                            <div class='text-body-secondary'>
                                <Text id='dyrejournal.idexx.clinicalNotes'>Clinical notes</Text>
                            </div>
                            <div style='white-space: pre-wrap;'>{order.clinicalNotes}</div>
                        </div>
                    )}
                    {resultCount > 0 && (
                        <div class='col-12'>
                            <span class='badge bg-success'>
                                <i class='fa-solid fa-vial me-1' />
                                <Text id='dyrejournal.idexx.resultsLinked' fields={{ count: resultCount }}>
                                    Results received ({resultCount})
                                </Text>
                            </span>
                        </div>
                    )}
                    {(uiUrl || (order.orderType === 'reference_lab' && pdfUrl)) && (
                        <div class='col-12 mt-1 d-flex flex-wrap gap-2'>
                            {uiUrl && (
                                <a href={uiUrl} target='_blank' rel='noopener noreferrer' class='btn btn-sm btn-outline-secondary'>
                                    <i class='fa-solid fa-arrow-up-right-from-square me-1' />
                                    <Text id='dyrejournal.idexx.viewInIdexx'>Open in VetConnect Plus</Text>
                                </a>
                            )}
                            {order.orderType === 'reference_lab' && pdfUrl && (
                                <a href={pdfUrl} target='_blank' rel='noopener noreferrer' class='btn btn-sm btn-outline-primary'>
                                    <i class='fa-solid fa-file-pdf me-1' />
                                    <Text id='dyrejournal.idexx.printRequisition'>Print requisition (ship with sample)</Text>
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { journal, cancelOrderTitle } = this.props;
        const { orders, loading, expandedId } = this.state;
        // Combine journal-side ids with any newly-created orders held only in
        // local state so the refresh button + "no orders" empty state both
        // reflect reality after handleOrderCreated runs.
        const orderIds = Array.from(new Set([
            ...((journal?.idexxOrders || []).map(Number).filter(Boolean)),
            ...((orders || []).map(o => o && o.id).filter(Boolean)),
        ]));

        return (
            <div class='mb-3'>
                <div class='d-flex justify-content-between align-items-center mb-2'>
                    <small class='fw-bold text-body-secondary'>
                        <i class='fa-duotone fa-flask-vial me-1' />
                        <Text id='dyrejournal.idexx.labOrders'>IDEXX Lab Orders</Text>
                    </small>
                    <div>
                        {orderIds.length > 0 && (
                            <Localizer>
                                <button
                                    class='btn btn-sm btn-outline-secondary me-1'
                                    onClick={() => this.loadOrders()}
                                    disabled={loading}
                                    title={<Text id='dyrejournal.idexx.refreshOrders'>Refresh status from IDEXX</Text>}
                                >
                                    <i class={`fa-solid ${loading ? 'fa-spinner fa-spin' : 'fa-rotate'}`} />
                                </button>
                            </Localizer>
                        )}
                        <button
                            class='btn btn-sm btn-outline-primary'
                            onClick={this.openOrderForm}
                        >
                            <i class='fa-solid fa-plus me-1' />
                            <Text id='dyrejournal.idexx.newOrder'>New Order</Text>
                        </button>
                    </div>
                </div>

                {loading && (
                    <div class='text-center text-body-secondary py-2'>
                        <i class='fa-solid fa-spinner fa-spin me-1' />
                        <small>
                            <Text id='dyrejournal.idexx.loadingOrders'>Loading orders...</Text>
                        </small>
                    </div>
                )}

                {!loading && orderIds.length === 0 && (
                    <div class='text-body-secondary small'>
                        <Text id='dyrejournal.idexx.noOrders'>No lab orders for this journal.</Text>
                    </div>
                )}

                {!loading && orders.map((order) => {
                    const badge = STATUS_BADGES[order.status] || STATUS_BADGES.pending;
                    const shortTests = (order.tests || []).map(t => t.testCode || t.name || t).join(', ');
                    const canCancel = order.status === 'pending' || order.status === 'submitted';
                    // Include time — multiple orders for the same patient on
                    // the same day are common, so date alone makes the rows
                    // indistinguishable in the collapsed view.
                    const orderDate = order.submittedDate
                        ? new Date(order.submittedDate).toLocaleString(undefined, {
                            year: 'numeric', month: 'numeric', day: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                        })
                        : '';
                    const isExpanded = expandedId === order.id;
                    // Surface the requisition PDF link on the collapsed row
                    // too — vets shouldn't have to expand the card just to
                    // find the form they need to ship with the sample.
                    const collapsedPdfUrl = order.orderType === 'reference_lab'
                        ? (order.idexxPdfURL || order.idexxResponse?.pdfURL || '')
                        : '';

                    return (
                        <div
                            key={order.id}
                            class='card card-body py-2 px-3 mb-2'
                            style='cursor: pointer;'
                            onClick={() => this.toggleExpanded(order.id)}
                        >
                            <div class='d-flex justify-content-between align-items-start'>
                                <div class='flex-grow-1'>
                                    <small class='fw-bold'>
                                        <i class={`fa-solid ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} me-2 text-body-secondary`} />
                                        {shortTests || <Text id='dyrejournal.idexx.labOrder'>Lab Order</Text>}
                                    </small>
                                    <div>
                                        <span class={`badge ${badge.class} me-1`}>
                                            <i class={`fa-solid ${badge.icon} me-1`} />
                                            <Text id={badge.labelId}>{badge.labelFallback}</Text>
                                        </span>
                                        <small class='text-body-secondary'>{orderDate}</small>
                                    </div>
                                    {order.idexxOrderId && !isExpanded && (
                                        <small class='text-body-secondary'>
                                            <Text id='dyrejournal.idexx.idexxIdInline'>IDEXX ID:</Text> {order.idexxOrderId}
                                        </small>
                                    )}
                                    {collapsedPdfUrl && (
                                        <div class='mt-1'>
                                            <a
                                                href={collapsedPdfUrl}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                class='btn btn-sm btn-outline-primary'
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <i class='fa-solid fa-file-pdf me-1' />
                                                <Text id='dyrejournal.idexx.printRequisition'>Print requisition (ship with sample)</Text>
                                            </a>
                                        </div>
                                    )}
                                </div>
                                {canCancel && (
                                    <Localizer>
                                        <button
                                            class='btn btn-sm btn-outline-danger'
                                            onClick={(e) => this.handleCancel(e, order.id)}
                                            title={cancelOrderTitle}
                                        >
                                            <i class='fa-solid fa-times' />
                                        </button>
                                    </Localizer>
                                )}
                            </div>
                            {isExpanded && this.renderDetails(order)}
                        </div>
                    );
                })}
            </div>
        );
    }
}

export default IdexxOrderStatus;
